import { useCallback, useEffect, useMemo, useRef, useState, type FormEvent } from "react";
import { Link, useNavigate, useParams, useLocation } from "react-router-dom";
import { api } from "../api/apiClient";
import type {
  CatalogItem,
  Color,
  DefaultUom,
  ItemCategory,
  SubCategory,
  TypedMoneyBasis,
  UpdateItemPayload,
} from "../api/types";
import { CREATE_NEW_CATEGORY, CategoryPicker } from "../components/CategoryPicker";
import { PhotoInput } from "../components/PhotoInput";
import { BarcodeField } from "../components/BarcodeField";
import { isVesselItemCategory } from "../utils/vesselItemCategory";
import { SegmentedControl } from "../components/ui/SegmentedControl";
import { cost } from "../i18n/intl";
import { IVA_TAKEN_OUT_HINT, ivaBasisOptions } from "../utils/purchaseMoneyText";
import { basisAfterEdit, basisForPrefill } from "../utils/typedMoneyBasis";

export function ItemEditPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [item, setItem] = useState<CatalogItem | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  // Form state, populated from item once it loads.
  /*
   * The items list keeps its filters in the query string, and the row that
   * opened this page carried them here. Returning to a bare `/app/items`
   * threw them away, which is what made "pick Flowers, open one, come back"
   * land on an unfiltered list.
   */
  const location = useLocation();
  const backToItems = { pathname: "/app/items", search: location.search };

  const [name, setName] = useState("");
  const [producer, setProducer] = useState("");
  const [description, setDescription] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [subCategoryId, setSubCategoryId] = useState("");
  const [reorderThreshold, setReorderThreshold] = useState("");
  const [sku, setSku] = useState("");
  const [barcode, setBarcode] = useState("");
  const [defaultPurchaseUnitCostEur, setDefaultPurchaseUnitCostEur] = useState("");
  /** G-121 S3: the stored cost is before IVA, so a filled box opens on Before IVA. */
  const [purchaseCostBasis, setPurchaseCostBasis] = useState<TypedMoneyBasis | null>(null);
  const [defaultSaleUnitPriceEur, setDefaultSaleUnitPriceEur] = useState("");
  const [isActive, setIsActive] = useState(true);
  const [newCategoryLabel, setNewCategoryLabel] = useState("");
  // G-30: a new category must carry a VAT rate; 21 is the general default.
  const [newCategoryVatPercent, setNewCategoryVatPercent] = useState("21");
  const [newCategoryTargetMarginPercent, setNewCategoryTargetMarginPercent] =
    useState("50");
  const [categoryListVersion, setCategoryListVersion] = useState(0);
  const [categories, setCategories] = useState<ItemCategory[]>([]);
  const [colors, setColors] = useState<Color[]>([]);
  const [subCategories, setSubCategories] = useState<SubCategory[]>([]);
  const [colorId, setColorId] = useState("");
  const [diameterCm, setDiameterCm] = useState("");
  const [depthCm, setDepthCm] = useState("");
  const [defaultUom, setDefaultUom] = useState<DefaultUom>("UNIT");

  // Image replacement state.
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [existingImageUrl, setExistingImageUrl] = useState<string | null>(null);
  const heldPreviewRef = useRef<{
    id: string;
    size: "thumb" | "full";
    version: string;
  } | null>(null);

  const releaseHeldPreview = useCallback(() => {
    const h = heldPreviewRef.current;
    if (!h) return;
    api.releaseItemImageBlobUrl(h.id, { size: h.size, version: h.version });
    heldPreviewRef.current = null;
  }, []);

  useEffect(() => () => releaseHeldPreview(), [releaseHeldPreview]);

  // Submit state.
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!id) return;
    releaseHeldPreview();
    setLoading(true);
    setLoadError(null);
    try {
      const fetched = await api.getItem(id);
      setItem(fetched);
      setName(fetched.name);
      setProducer(fetched.producer ?? "");
      setDescription(fetched.description ?? "");
      setCategoryId(fetched.category.id);
      setSubCategoryId(fetched.subCategoryId ?? "");
      setReorderThreshold(fetched.reorderThreshold ?? "");
      setSku(fetched.sku ?? "");
      setBarcode(fetched.barcode ?? "");
      setDefaultPurchaseUnitCostEur(fetched.defaultPurchaseUnitCostEur ?? "");
      setPurchaseCostBasis(basisForPrefill(fetched.defaultPurchaseUnitCostEur));
      setDefaultSaleUnitPriceEur(fetched.defaultSaleUnitPriceEur ?? "");
      setIsActive(fetched.isActive);
      setColorId(fetched.colorId ?? "");
      setDefaultUom(fetched.defaultUom);
      setDiameterCm(fetched.diameterCm ?? "");
      setDepthCm(fetched.depthCm ?? "");
      if (fetched.imageKey) {
        const url = await api.fetchItemImageBlobUrl(fetched.id, {
          size: "thumb",
          version: fetched.imageKey,
        });
        heldPreviewRef.current = { id: fetched.id, size: "thumb", version: fetched.imageKey };
        setExistingImageUrl(url);
      } else {
        setExistingImageUrl(null);
      }
    } catch (e: unknown) {
      setLoadError(e instanceof Error ? e.message : "Failed to load item");
    } finally {
      setLoading(false);
    }
  }, [id, releaseHeldPreview]);

  useEffect(() => {
    void load();
  }, [load]);

  const loadCategories = useCallback(async () => {
    try {
      setCategories(await api.listCategories());
    } catch {
      setCategories([]);
    }
  }, []);

  useEffect(() => {
    void loadCategories();
  }, [loadCategories, categoryListVersion]);

  useEffect(() => {
    void api
      .listColors()
      .then(setColors)
      .catch(() => setColors([]));
  }, []);

  const selectedCategoryCode = useMemo(() => {
    if (!item) return undefined;
    const fromList = categories.find((c) => c.id === categoryId)?.code;
    if (fromList !== undefined) return fromList;
    // Avoid stale item.category when the user picked a new category before the list resolves.
    if (categoryId === item.category.id) return item.category.code;
    return undefined;
  }, [categories, categoryId, item]);
  const showVesselDimensions =
    selectedCategoryCode !== undefined && isVesselItemCategory(selectedCategoryCode);

  useEffect(() => {
    if (!selectedCategoryCode) {
      setSubCategories([]);
      return;
    }
    void api
      .listSubCategories(selectedCategoryCode)
      .then(setSubCategories)
      .catch(() => setSubCategories([]));
  }, [selectedCategoryCode]);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!item) return;
    setSubmitting(true);
    setError(null);
    setSuccess(null);
    try {
      let categoryIdForItem = categoryId;
      if (categoryId === CREATE_NEW_CATEGORY) {
        const label = newCategoryLabel.trim();
        if (!label) throw new Error("Enter a name for the new category");
        const vatRate = Number(newCategoryVatPercent);
        if (
          !newCategoryVatPercent.trim() ||
          !Number.isFinite(vatRate) ||
          vatRate < 0 ||
          vatRate > 100
        ) {
          throw new Error("Enter a VAT rate between 0 and 100 for the new category");
        }
        // G-45: bounded BELOW 100 — the price formula divides by (1 - m/100),
        // so 100 is an infinite price and above it the price goes negative.
        const targetMargin = Number(newCategoryTargetMarginPercent);
        if (
          !newCategoryTargetMarginPercent.trim() ||
          !Number.isFinite(targetMargin) ||
          targetMargin < 0 ||
          targetMargin >= 100
        ) {
          throw new Error(
            "Enter a target margin of 0 or more and under 100 for the new category",
          );
        }
        const newCategory = await api.createCategory({
          label,
          vatRatePercent: vatRate,
          targetMarginPercent: targetMargin,
        });
        categoryIdForItem = newCategory.id;
        setCategoryListVersion((v) => v + 1);
      }

      const payload: UpdateItemPayload = {
        name: name.trim(),
        producer: producer.trim() || null,
        description: description.trim() || null,
        categoryId: categoryIdForItem,
        sku: sku.trim() || null,
        barcode: barcode.trim() || null,
        // Both prices are optional: an empty box is sent as null and means
        // "work it out" — cost from the deliveries, selling price from cost ×
        // the category's margin (G-45). Only a typed value is checked.
        defaultPurchaseUnitCostEur: (() => {
          if (defaultPurchaseUnitCostEur.trim() === "") return null;
          const value = Number(defaultPurchaseUnitCostEur);
          if (!Number.isFinite(value) || value < 0) {
            throw new Error("Default purchase cost must be a non-negative number");
          }
          if (purchaseCostBasis === null) {
            throw new Error("Say whether the purchase cost is before IVA or includes IVA");
          }
          return value;
        })(),
        // G-121 S3: with a cost, its side of IVA (the server keeps the net).
        ...(defaultPurchaseUnitCostEur.trim() !== "" && purchaseCostBasis !== null
          ? { defaultPurchaseUnitCostBasis: purchaseCostBasis }
          : {}),
        defaultSaleUnitPriceEur: (() => {
          if (defaultSaleUnitPriceEur.trim() === "") return null;
          const value = Number(defaultSaleUnitPriceEur);
          if (!Number.isFinite(value) || value < 0) {
            throw new Error("Default selling price must be a non-negative number");
          }
          return value;
        })(),
        subCategoryId: subCategoryId || null,
        reorderThreshold: (() => {
          const t = reorderThreshold.trim();
          if (t === "") return null;
          const n = Number(t);
          if (!Number.isFinite(n) || n < 0) {
            throw new Error("Reorder threshold must be a non-negative number");
          }
          return n;
        })(),
        isActive,
        colorId: colorId || null,
        defaultUom,
      };

      if (showVesselDimensions) {
        const parseDim = (s: string, label: string): number | null => {
          const t = s.trim();
          if (t === "") return null;
          const n = Number(t);
          if (!Number.isFinite(n) || n < 0 || n > 10_000) {
            throw new Error(`${label} (cm) must be between 0 and 10000`);
          }
          return n;
        };
        payload.diameterCm = parseDim(diameterCm, "Diameter");
        payload.depthCm = parseDim(depthCm, "Depth");
      }

      const updated = imageFile
        ? await api.updateItemWithImage(item.id, payload, imageFile)
        : await api.updateItem(item.id, payload);

      // Re-read for what an empty price box now works out to — only the
      // single-item read carries it. The save has already succeeded, so a
      // failed re-read must not be reported as a failed save.
      setItem(await api.getItem(updated.id).catch(() => updated));
      setColorId(updated.colorId ?? "");
      setSubCategoryId(updated.subCategoryId ?? "");
      setDiameterCm(updated.diameterCm ?? "");
      setDepthCm(updated.depthCm ?? "");
      setDefaultPurchaseUnitCostEur(updated.defaultPurchaseUnitCostEur ?? "");
      setPurchaseCostBasis(basisForPrefill(updated.defaultPurchaseUnitCostEur));
      setDefaultSaleUnitPriceEur(updated.defaultSaleUnitPriceEur ?? "");
      setSuccess(
        purchaseCostBasis === "INCLUDES_IVA" && updated.defaultPurchaseUnitCostEur
          ? `Changes saved. The purchase cost is kept before IVA: ${cost(updated.defaultPurchaseUnitCostEur)}.`
          : "Changes saved.",
      );
      setImageFile(null);
      if (updated.imageKey) {
        releaseHeldPreview();
        // G-116: versioned by the NEW key, so the just-saved photo is fetched
        // rather than the browser's five-minute copy of the old one.
        const url = await api.fetchItemImageBlobUrl(updated.id, { version: updated.imageKey });
        heldPreviewRef.current = { id: updated.id, size: "full", version: updated.imageKey };
        setExistingImageUrl(url);
      } else {
        releaseHeldPreview();
        setExistingImageUrl(null);
      }
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Could not save changes");
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggleActive = async () => {
    if (!item) return;
    const nextActive = !item.isActive;
    const confirmMsg = nextActive
      ? "Reactivate this item?"
      : "Archive this item? It will be hidden from default lists but history is preserved.";
    if (!window.confirm(confirmMsg)) return;
    setSubmitting(true);
    setError(null);
    setSuccess(null);
    try {
      const updated = await api.updateItem(item.id, { isActive: nextActive });
      setItem(updated);
      setIsActive(updated.isActive);
      setSuccess(updated.isActive ? "Item reactivated." : "Item archived.");
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "Could not update status");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <section>
        <p style={{ color: "var(--color-text-muted)" }}>Loading…</p>
      </section>
    );
  }

  if (loadError || !item) {
    return (
      <section style={{ display: "flex", flexDirection: "column", gap: "var(--space-3)" }}>
        <Link to={backToItems}>← Back to items</Link>
        <div className="alert error">{loadError ?? "Item not found"}</div>
      </section>
    );
  }

  const creatingNewCategory = categoryId === CREATE_NEW_CATEGORY;
  /** G-121 S3: the rate the cost's IVA switch names — the category being saved. */
  const costIvaRate: string | undefined = creatingNewCategory
    ? newCategoryVatPercent
    : (categories.find((c) => c.id === categoryId)?.vatRatePercent ?? item.category.vatRatePercent);
  // What each price box means when it is left empty — from the saved item.
  const lastDeliveryCost =
    item.lastDeliveryUnitCostEur != null ? Number(item.lastDeliveryUnitCostEur).toFixed(2) : null;
  const derivedSalePrice =
    item.derivedSaleUnitPriceEur != null ? Number(item.derivedSaleUnitPriceEur).toFixed(2) : null;

  return (
    <section style={{ display: "flex", flexDirection: "column", gap: "var(--space-4)" }}>
      <div>
        <Link to={backToItems}>← Back to items</Link>
      </div>

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
        <div>
          <h2 className="page-title">Edit item</h2>
          <p className="page-subtitle">
            {item.isPerishable ? "Perishable" : "Non-perishable"} ·{" "}
            <strong>{defaultUom}</strong>
            {item.isActive ? null : (
              <>
                {" · "}
                <span className="badge muted">Archived</span>
              </>
            )}
          </p>
        </div>
        <button
          type="button"
          className="button tinted"
          onClick={handleToggleActive}
          disabled={submitting}
        >
          {item.isActive ? "Archive item" : "Reactivate item"}
        </button>
      </div>

      <form className="card" onSubmit={handleSubmit}>
        {error ? <div className="alert error full">{error}</div> : null}
        {success ? <div className="alert success full">{success}</div> : null}

        <div className="form-grid">
          <div className="field full">
            <label htmlFor="name">Name *</label>
            <input
              id="name"
              type="text"
              required
              maxLength={200}
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>

          <div className="field full">
            <label htmlFor="producer">Producer / grower</label>
            <input
              id="producer"
              type="text"
              maxLength={200}
              value={producer}
              onChange={(e) => setProducer(e.target.value)}
            />
            <p className="field-hint">
              Who grew or made it, as the supplier named it. Filled in from the delivery note
              when the item came from one.
            </p>
          </div>
          <div className="field full">
            <label htmlFor="description">Notes about this item</label>
            <textarea
              id="description"
              rows={3}
              maxLength={2000}
              value={description}
              placeholder="Smells strong, keep it off the counter…"
              onChange={(e) => setDescription(e.target.value)}
            />
            <p className="field-hint">
              Anything a person needs to know that no other field says. Shown
              on the kiosk item card, where it can also be edited.
            </p>
          </div>

          <CategoryPicker
            value={categoryId}
            onChange={setCategoryId}
            newCategoryLabel={newCategoryLabel}
            onNewCategoryLabelChange={setNewCategoryLabel}
            newCategoryVatPercent={newCategoryVatPercent}
            newCategoryTargetMarginPercent={newCategoryTargetMarginPercent}
            onNewCategoryTargetMarginPercentChange={setNewCategoryTargetMarginPercent}
            onNewCategoryVatPercentChange={setNewCategoryVatPercent}
            listVersion={categoryListVersion}
            required
          />

          {showVesselDimensions ? (
            <>
              <div className="field">
                <label htmlFor="edit-diameterCm">Diameter (cm)</label>
                <input
                  id="edit-diameterCm"
                  type="number"
                  min={0}
                  max={10_000}
                  step="any"
                  value={diameterCm}
                  onChange={(e) => setDiameterCm(e.target.value)}
                />
                <p className="field-hint">Opening / outer diameter for buckets and vases.</p>
              </div>
              <div className="field">
                <label htmlFor="edit-depthCm">Depth (cm)</label>
                <input
                  id="edit-depthCm"
                  type="number"
                  min={0}
                  max={10_000}
                  step="any"
                  value={depthCm}
                  onChange={(e) => setDepthCm(e.target.value)}
                />
              </div>
            </>
          ) : null}

          <div className="field full">
            <label htmlFor="subCat">Flower type</label>
            <select
              id="subCat"
              value={subCategoryId}
              onChange={(e) => setSubCategoryId(e.target.value)}
              disabled={!subCategories.length}
            >
              <option value="">— None —</option>
              {subCategories.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.label}
                </option>
              ))}
            </select>
          </div>

          <div className="field">
            <label htmlFor="colorId">Color</label>
            <select
              id="colorId"
              value={colorId}
              onChange={(e) => setColorId(e.target.value)}
            >
              <option value="">— None —</option>
              {colors.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>

          <div className="field">
            <label htmlFor="reorder">Reorder threshold</label>
            <input
              id="reorder"
              type="number"
              min={0}
              step="0.001"
              value={reorderThreshold}
              onChange={(e) => setReorderThreshold(e.target.value)}
            />
            <p className="field-hint">Optional minimum-stock signal in {defaultUom}.</p>
          </div>

          <div className="field">
            <label htmlFor="defaultUom">Unit of measure</label>
            <select
              id="defaultUom"
              value={defaultUom}
              onChange={(e) => setDefaultUom(e.target.value as DefaultUom)}
            >
              <option value="STEM">STEM</option>
              <option value="UNIT">UNIT</option>
              <option value="CM">CM</option>
              <option value="ROLL">ROLL</option>
            </select>
            <p className="field-hint">
              Changing UOM after stock has been received may affect inventory
              calculations. Only change this if no active batches exist for this item.
            </p>
          </div>

          <div className="field">
            <label htmlFor="edit-defaultPurchaseUnitCostEur">Purchase cost (€ per {defaultUom})</label>
            <input
              id="edit-defaultPurchaseUnitCostEur"
              type="number"
              min={0}
              step="0.0001"
              placeholder={lastDeliveryCost ?? undefined}
              value={defaultPurchaseUnitCostEur}
              onChange={(e) => {
                setDefaultPurchaseUnitCostEur(e.target.value);
                setPurchaseCostBasis((b) => basisAfterEdit(b, e.target.value));
              }}
            />
            {/* G-121 S3 (OD-140): which side of IVA, at the category's rate. */}
            {defaultPurchaseUnitCostEur.trim() !== "" && costIvaRate !== undefined ? (
              <div className="recv-iva-basis">
                <SegmentedControl
                  options={ivaBasisOptions(costIvaRate)}
                  value={purchaseCostBasis}
                  onChange={setPurchaseCostBasis}
                  ariaLabel="Is the purchase cost before IVA or does it include IVA?"
                />
              </div>
            ) : null}
            <p className="field-hint">
              {purchaseCostBasis === "INCLUDES_IVA" ? `${IVA_TAKEN_OUT_HINT} ` : ""}
              {lastDeliveryCost
                ? `Last delivery cost €${lastDeliveryCost}. Deliveries always win; a typed cost is used only while there are none.`
                : "Optional. Deliveries set the real cost; a typed cost is used only until the first one arrives."}
            </p>
          </div>

          <div className="field">
            <label htmlFor="edit-defaultSaleUnitPriceEur">Selling price (€ per {defaultUom})</label>
            <input
              id="edit-defaultSaleUnitPriceEur"
              type="number"
              min={0}
              step="0.01"
              placeholder={derivedSalePrice ?? undefined}
              value={defaultSaleUnitPriceEur}
              onChange={(e) => setDefaultSaleUnitPriceEur(e.target.value)}
            />
            <p className="field-hint">
              {defaultSaleUnitPriceEur.trim() !== ""
                ? `Your price · overrides the suggested price${derivedSalePrice ? ` (€${derivedSalePrice})` : ""} · empty the box to go back to it`
                : derivedSalePrice
                  ? `Suggested price €${derivedSalePrice} (cost, target margin, IVA) · type a price only to override it`
                  : "Suggested price once the item has a cost · type a price only to override it"}
            </p>
          </div>

          <div className="field">
            <label htmlFor="sku">SKU</label>
            <input
              id="sku"
              type="text"
              maxLength={64}
              value={sku}
              onChange={(e) => setSku(e.target.value)}
            />
          </div>

          <BarcodeField id="barcode" value={barcode} onChange={setBarcode} />

          <div className="field full">
            <label className="field-checkbox" style={{ display: "flex", gap: "var(--space-2)", alignItems: "center" }}>
              <input
                type="checkbox"
                checked={isActive}
                onChange={(e) => setIsActive(e.target.checked)}
              />
              <span>Active (uncheck to archive)</span>
            </label>
          </div>

          <div className="field full">
            <label>Photo</label>
            {existingImageUrl && !imageFile ? (
              <div className="item-image-preview">
                <img src={existingImageUrl} alt="" />
                <p className="field-hint">
                  Current photo. Choose a new file or take a photo below to replace it.
                </p>
              </div>
            ) : null}
            <PhotoInput value={imageFile} onChange={setImageFile} />
          </div>
        </div>

        <div className="button-row">
          <button
            className="button"
            type="submit"
            disabled={
              submitting ||
              !name ||
              !categoryId ||
              (creatingNewCategory && !newCategoryLabel.trim())
            }
          >
            {submitting ? "Saving…" : "Save changes"}
          </button>
          <button
            type="button"
            className="button tinted"
            onClick={() => navigate(backToItems)}
            disabled={submitting}
          >
            Cancel
          </button>
        </div>
      </form>
    </section>
  );
}
